import { createAsyncThunk } from "@reduxjs/toolkit";
import * as LoginApi from "../../../api/loginApi";
import { trackEvent } from "../../../utils/tracking";
import { TRACKING_EVENTS } from "../../../utils/trackingDefinitions";

export const USERNAME_SESSION_KEY = "loginUsername";

export const isAlreadyAuthenticated = createAsyncThunk(
	"login/isAlreadyAuthenticated",
	async ({ returnUrl }: { returnUrl: string }) => {
		try {
			const response = await LoginApi.isAlreadyAuthenticated({
				returnUrl,
			});

			if (response.isAuthenticated) {
				window.location.href = `/alreadyloggedin?returnUrl=${encodeURIComponent(
					returnUrl,
				)}`;
			}
		} catch (error) {
			return { error: "komid.general.network.error" };
		}
	},
);

export const checkIfEmailExists = createAsyncThunk(
	"login/checkIfEmailExists",
	async ({ email, returnUrl }: { email: string; returnUrl: string }) => {
		try {
			const response = await LoginApi.checkIfEmailExists({
				email,
				returnUrl,
			});

			if (response.isSuccess && response.data.exists) {
				trackEvent({
					event: TRACKING_EVENTS.LOGIN.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.LOGIN.APP,
					key: "action",
					value: TRACKING_EVENTS.LOGIN.EVENT.CHECK_USERNAME_EXISTS,
				});

				sessionStorage.setItem(USERNAME_SESSION_KEY, email);
			}

			return {
				isSuccess: response.isSuccess,
				error: response.errors?.[0],
				result: response,
			};
		} catch (error) {
			return { error: "komid.general.network.error" };
		}
	},
);

export const verifyLogin = createAsyncThunk(
	"login/verifyLogin",
	async ({
		username,
		password,
		returnUrl,
		rememberLogin,
	}: {
		username: string;
		password: string;
		returnUrl: string;
		rememberLogin: boolean;
	}) => {
		try {
			const response = await LoginApi.verifyLogin({
				username,
				password,
				returnUrl,
				rememberLogin,
			});

			if (response.isSuccess) {
				if (response?.requiresTwoFactor) {
					window.location.href = `/twofactor?returnUrl=${
						response.returnUrl
					}&username=${encodeURIComponent(username)}`;
				} else {
					trackEvent({
						event: TRACKING_EVENTS.LOGIN.EVENT.USER_INTERACTION,
						target: TRACKING_EVENTS.LOGIN.APP,
						key: "action",
						value: TRACKING_EVENTS.LOGIN.EVENT.VERIFY_LOGIN,
					});

					sessionStorage.removeItem(USERNAME_SESSION_KEY);

					window.location.href = response.returnUrl;
				}
			}

			return {
				isSuccess: response.isSuccess,
				isWrongPassword: response.isWrongPassword,
				error: response.errors?.[0],
				errorLevel: "error",
				messageValues: response?.isUserLocked
					? {
							destination: `/password/forgot?returnUrl=${encodeURIComponent(
								returnUrl,
							)}&username=${encodeURIComponent(username)}`,
						}
					: {},
				result: response,
			};
		} catch (error) {
			return { error: "komid.general.network.error" };
		}
	},
);

export const passwordlessLogin = createAsyncThunk(
	"login/passwordlessLogin",
	async ({
		username,
		returnUrl,
		rememberLogin,
	}: {
		username: string;
		returnUrl: string;
		rememberLogin: boolean;
	}) => {
		try {
			const response = await LoginApi.passwordLessLogin({
				username,
				rememberLogin,
				returnUrl,
			});

			if (response.isSuccess) {
				trackEvent({
					event: TRACKING_EVENTS.LOGIN.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.LOGIN.APP,
					key: "action",
					value: TRACKING_EVENTS.LOGIN.EVENT.REQUEST_PASSWORDLESS_LOGIN,
				});
			}

			return {
				isSuccess: response.isSuccess,
				error: response.errors?.[0],
				errorLevel: "error",
				messageValues: response?.isUserLocked
					? {
							destination: `/password/forgot?returnUrl=${encodeURIComponent(
								returnUrl,
							)}&username=${encodeURIComponent(username)}`,
						}
					: {},
				result: response,
			};
		} catch (error) {
			return { error: "komid.general.network.error" };
		}
	},
);
